/**
 * The password policy, shared by the auth service (its zod `password()`
 * request schema) and the web client's inline form validation. One constant
 * on both sides of the REST boundary means the client's feedback can never
 * disagree with what the server enforces.
 */
export const PASSWORD_MIN_LENGTH = 8;

// The most guessed passwords that the length rule alone lets through. A short
// list on purpose: it refuses the words an attacker tries first, and it is not
// a strength meter.
const COMMON_PASSWORDS: ReadonlySet<string> = new Set([
  "00000000",
  "11111111",
  "12341234",
  "12345678",
  "123456789",
  "1234567890",
  "1q2w3e4r",
  "87654321",
  "abc12345",
  "abcd1234",
  "admin123",
  "baseball",
  "changeme",
  "football",
  "iloveyou",
  "letmein1",
  "passw0rd",
  "password",
  "password!",
  "password1",
  "password12",
  "password123",
  "princess",
  "qwerty12",
  "qwerty123",
  "qwertyuiop",
  "starwars",
  "sunshine",
  "superman",
  "trustno1",
  "welcome1",
  "whatever",
  "zaq12wsx",
]);

/** Whether a password is on the common list, compared case-insensitively. */
export function isCommonPassword(password: string): boolean {
  return COMMON_PASSWORDS.has(password.trim().toLowerCase());
}

export const COMMON_PASSWORD_MESSAGE = "This password is too common. Choose a different one.";

/**
 * Why a NEW password breaks the policy, or null when it passes. A check on an
 * existing password (sign-in, proving identity) uses only the length rule,
 * because an old password may predate the common list.
 */
export function newPasswordProblem(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (isCommonPassword(password)) return COMMON_PASSWORD_MESSAGE;
  return null;
}
